import { useDataProvider } from "@hooks";
import { Loader, Select, SelectItem, SelectProps } from "@mantine/core";
import { addressService } from "@services";
import { IconSearch } from "@tabler/icons-react";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

export interface ILocationFieldProps
  extends Omit<SelectProps, "data" | "onChange"> {
  minimumInputLength?: number;
  onChange?: (value?: ILocationItem) => void;
}

export interface ILocationItem extends SelectItem {
  id: string;
  postcode: string;
  state: string;
  suburb: string;
}

export const LocationField = forwardRef<HTMLInputElement, ILocationFieldProps>(
  ({ minimumInputLength, onChange, value, placeholder, ...rest }, ref) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState(value || "");
    const [location, setLocation] = useState<ILocationItem>();
    const [data, setData] = useState<ILocationItem[]>([]);
    const dataProvider = useDataProvider();
    const dataProviderInstance = useMemo(() => dataProvider(), [dataProvider]);
    const _addressService = useMemo(() => addressService(dataProviderInstance), [dataProviderInstance]);

    useEffect(() => setSearchValue(value || ""), [value]);

    const searchAsync = useCallback((inputValue: string) => {
      return new Promise<any[]>((resolve, reject) => {
        if (minimumInputLength && inputValue.length < minimumInputLength) {
          reject();
        } else {
          _addressService
            .getPostalSuburb(inputValue)
            .then((json) => {
              resolve(json as ILocationItem[]);
            })
            .catch((err: any) => {
              reject(err.message);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      });
    }, [_addressService, minimumInputLength]);

    const handleChange = (val: string) => {
      setSearchValue(val);
      setData([]);

      if (!val || val.trim().length === 0 || val.includes("@")) {
        setLoading(false);
      } else {
        setLoading(true);
        searchAsync(val)
          .then((locations: ILocationItem[]) => {
            setData(locations);
          })
          .finally(() => setLoading(false));
      }
    };

    return (
      <>
        <Select
          value={location?.value}
          placeholder={value || placeholder}
          data={data}
          searchValue={searchValue}
          searchable
          onSearchChange={handleChange}
          clearable
          rightSection={
            loading ? <Loader size={16} /> : <IconSearch size={16} />
          }
          onChange={(newValue) => {
            const selected = data.find((x) => x.value === newValue);
            if (selected) {
              setSearchValue(selected.value);
            }
            setLocation(selected);
            onChange?.(selected);
          }}
          {...rest}
          ref={ref}
        />
      </>
    );
  },
);
